import { AnalysisResponse } from '@futrli-sdk/predict_analysis';
import { IntlShape } from 'react-intl';

import {
  formatMetricValue,
  formatPeriodLabel,
  mapStatusToVariant,
  getKpiLabel
} from './format-utils';
import {
  mapHealthScore,
  mapRevenueChart,
  mapCostStructure,
  mapLiquidityPanel,
  mapInsights,
  mapExecutiveSummary
} from './shared-mappers';
import { MappedAccountantData } from './types';

export const mapToAccountantData = (analysis: AnalysisResponse['analysis'], intl: IntlShape): MappedAccountantData => {
  const { currency_code } = analysis;

  const kpiMetrics = (analysis.kpi_banner_cards ?? []).map(card => {
    const contextLabel = `${formatPeriodLabel(card.period)} ${card.is_forecast
      ? intl.formatMessage({ id: 'aiInsights.kpi.forecast', defaultMessage: 'forecast' })
      : intl.formatMessage({ id: 'aiInsights.kpi.actual', defaultMessage: 'actual' })}`;

    const hasYoyChange = card.yoy_change !== null && card.yoy_change !== undefined;
    const trend = hasYoyChange ? {
      direction: (card.yoy_change > 0 ? 'up' : 'down') as 'up' | 'down',
      text: [
        card.yoy_change > 0
          ? intl.formatMessage({ id: 'common.up',   defaultMessage: 'Up'   })
          : intl.formatMessage({ id: 'common.down', defaultMessage: 'Down' }),
        `${Math.abs(card.yoy_change * 100).toFixed(1)}%`,
        intl.formatMessage({ id: 'aiInsights.kpi.yearOverYear', defaultMessage: 'year-over-year' })
      ].join(' ')
    } : undefined;

    return {
      label: getKpiLabel(card.kpi_key, 'accountant', intl),
      contextLabel,
      value: formatMetricValue(card.value, card.format, currency_code, true),
      trend,
      status: mapStatusToVariant(card.status)
    };
  });

  const insights = mapInsights(
    analysis.insights,
    analysis.health_score?.score_components,
    currency_code,
    intl
  );

  return {
    analysisId: analysis.id,
    analysisVersion: analysis.version,
    periodLabel: intl.formatMessage(
      { id: 'aiInsights.accountant.periodLabel', defaultMessage: '{periodStart} to {periodEnd} forecast' },
      { periodStart: analysis.period_start, periodEnd: analysis.period_end }
    ),
    createdAt: analysis.created,
    healthScore: analysis.health_score ? mapHealthScore(analysis.health_score, intl) : undefined,
    kpiMetrics,
    revenueChart: analysis.revenue_chart
      ? mapRevenueChart(
        analysis.revenue_chart,
        currency_code,
        intl.formatMessage({ id: 'aiInsights.accountant.revenueTrend', defaultMessage: 'Revenue Trend' }),
      )
      : undefined,
    costStructure: analysis.cost_structure
      ? mapCostStructure(
        analysis.cost_structure,
        currency_code,
        intl.formatMessage({ id: 'aiInsights.costStructure', defaultMessage: 'Cost structure - Operating Expenses' }),
        intl.formatMessage({ id: 'aiInsights.costStructureTotalLabel', defaultMessage: '/ year (fixed)' })
      )
      : undefined,
    liquidity: analysis.liquidity_panel
      ? mapLiquidityPanel(
        analysis.liquidity_panel,
        currency_code,
        intl.formatMessage({ id: 'aiInsights.accountant.liquidityOverview', defaultMessage: 'Liquidity Overview' }),
        'accountant',
        intl
      )
      : undefined,
    insights,
    executiveSummary: mapExecutiveSummary(
      analysis.executive_summary,
      intl.formatMessage({ id: 'aiInsights.accountant.executiveSummary', defaultMessage: 'Executive Summary' }),
      intl
    )
  };
};
